import React, { useState, useRef, useEffect } from 'react';

const RangeSlider = ({
  min,
  max,
  value,
  onChange,
  label,
  className = ""
}) => {
  const sliderRef = useRef(null);
  const [isDragging, setIsDragging] = useState(null); // 'min', 'max', or null

  // Destructure with default values to handle potential issues with the value prop
  const [minValue, maxValue] = Array.isArray(value) && value.length === 2
    ? [Math.min(...value), Math.max(...value)] // Ensure min <= max
    : [min, max];

  // Calculate positions as percentages
  // Handle the case where max equals min to avoid division by zero
  const range = max - min;
  let minPercent = 0;
  let maxPercent = 100;

  if (range !== 0) {
    minPercent = ((minValue - min) / range) * 100;
    maxPercent = ((maxValue - min) / range) * 100;

    // Ensure percentages are within valid bounds
    minPercent = Math.max(0, Math.min(100, minPercent));
    maxPercent = Math.max(0, Math.min(100, maxPercent));
  }

  // Handle click on the track to determine which thumb to move when they overlap
  const handleTrackClick = (e) => {
    if (isDragging) return; // Don't interfere with dragging

    if (!sliderRef.current) return;

    const rect = sliderRef.current.getBoundingClientRect();
    const clickPosition = (e.clientX - rect.left) / rect.width;
    const clickValue = Math.round(clickPosition * (max - min) + min);

    // Calculate the current positions of both thumbs
    const minPos = ((minValue - min) / range) * 100;
    const maxPos = ((maxValue - min) / range) * 100;

    // If thumbs are overlapping
    if (minValue === maxValue) {
      // If both are at max, only move min
      if (minValue === max) {
        onChange([clickValue, maxValue]);
      }
      // If both are at min, only move max
      else if (minValue === min) {
        onChange([minValue, clickValue]);
      }
      // If both are at same value in middle, move based on click position relative to current value
      else {
        const currentValuePos = ((minValue - min) / range) * 100;
        const clickPos = clickPosition * 100;

        // If click is to the left of current value, move min, otherwise move max
        if (clickPos < currentValuePos) {
          onChange([clickValue, maxValue]);
        } else {
          onChange([minValue, clickValue]);
        }
      }
    } else {
      // Thumbs are not overlapping, determine which one to move based on proximity
      const minDistance = Math.abs(clickPosition * 100 - minPos);
      const maxDistance = Math.abs(clickPosition * 100 - maxPos);

      if (minDistance <= maxDistance) {
        // Move min thumb
        if (clickValue <= maxValue) {
          onChange([clickValue, maxValue]);
        } else {
          onChange([maxValue, clickValue]);
        }
      } else {
        // Move max thumb
        if (clickValue >= minValue) {
          onChange([minValue, clickValue]);
        } else {
          onChange([clickValue, minValue]);
        }
      }
    }
  };

  const handleMouseDown = (e, type) => {
    e.preventDefault();
    setIsDragging(type);
  };

  const handleMouseMove = (e) => {
    if (!isDragging || !sliderRef.current) return;

    const rect = sliderRef.current.getBoundingClientRect();
    const pos = Math.min(Math.max((e.clientX - rect.left) / rect.width, 0), 1);
    const newValue = Math.round(pos * (max - min) + min);

    if (isDragging === 'min') {
      // Allow min to go beyond max - when this happens, we swap their roles
      if (newValue <= maxValue) {
        onChange([newValue, maxValue]);
      } else {
        // If min is dragged past max, swap them so the thumb that was being dragged
        // maintains its position but becomes the max thumb
        onChange([maxValue, newValue]);
      }
    } else if (isDragging === 'max') {
      // Allow max to go before min - when this happens, we swap their roles
      if (newValue >= minValue) {
        onChange([minValue, newValue]);
      } else {
        // If max is dragged before min, swap them so the thumb that was being dragged
        // maintains its position but becomes the min thumb
        onChange([newValue, minValue]);
      }
    }
  };

  const handleMouseUp = () => {
    setIsDragging(null);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, minValue, maxValue, min, max]);

  return (
    <div className={`space-y-2 ${className}`}>
      <div className="text-center text-sm font-medium">
        {label}
      </div>
      <div
        ref={sliderRef}
        className="relative h-6 w-full"
        onClick={handleTrackClick} // Add click handler to the track
      >
        {/* Track */}
        <div className="absolute top-1/2 left-0 right-0 h-2 -translate-y-1/2 bg-gray-700 rounded-full"></div>

        {/* Selected range */}
        <div
          className="absolute top-1/2 h-2 -translate-y-1/2 bg-green-500 rounded-full"
          style={{
            left: `${Math.min(minPercent, maxPercent)}%`,
            width: `${Math.abs(maxPercent - minPercent)}%`
          }}
        ></div>

        {/* Min thumb */}
        <div
          className="absolute top-1/2 w-4 h-4 -mt-2 -ml-2 bg-green-500 rounded-full cursor-pointer"
          style={{ left: `${minPercent}%`, zIndex: isDragging === 'min' ? 20 : 10 }}
          onMouseDown={(e) => handleMouseDown(e, 'min')}
        ></div>

        {/* Max thumb */}
        <div
          className="absolute top-1/2 w-4 h-4 -mt-2 -ml-2 bg-green-500 rounded-full cursor-pointer"
          style={{ left: `${maxPercent}%`, zIndex: isDragging === 'max' ? 20 : 10 }}
          onMouseDown={(e) => handleMouseDown(e, 'max')}
        ></div>
      </div>
      <div className="flex justify-between text-xs text-muted-foreground">
        <span>{minValue}</span>
        <span>{maxValue}</span>
      </div>
    </div>
  );
};

export default RangeSlider;